<script setup lang="ts">
  import { MyNotification } from '@borg/types';
  import { MpIcon, MpText } from '@borg/ui';
  import { getJobLocation } from '@borg/core/utils/navigate';

  const props = defineProps<{
    item: MyNotification;
  }>();

  const emit = defineEmits<{
    (e: 'read', item: MyNotification): void;
    (e: 'delete', item: MyNotification): void;
  }>();

  const employerName = computed(() => {
    if (props.item.event === 'job_published' || props.item.event === 'job_published_by_company') {
      return props.item.payload.company.name;
    }
    return '';
  });

  const createdTime = computed(() => {
    try {
      return new Date(props.item.dateCreated);
    } catch (error) {
      return undefined;
    }
  });
</script>

<template>
  <div class="notification-item">
    <span
      :class="{
        'notification-item__dot': true,
        'notification-item__dot--active': !item.seenBySubscriber,
      }"
    />
    <div class="notification-item__text">
      <template v-if="item.event === 'job_published' || item.event === 'job_published_by_company'">
        <LinksLocale
          :to="
            getJobLocation(item.payload.uuid, item.payload.slug ?? 'unknown', {
              source: 'notifications',
            })
          "
        >
          <i18n-t
            keypath="MY_NOTIFICATIONS.NEW.AD"
            tag="div"
            scope="global"
            @click="emit('read', item)"
          >
            <template #employer>
              {{ employerName }}
            </template>
            <template #link>
              <b>{{ item.payload.title.name }}</b>
            </template>
          </i18n-t>
        </LinksLocale>
      </template>
      <MpText
        as="time"
        :datetime="createdTime"
        shade="40"
        no-margin
      >
        {{ $d(...localeDateTimeFormat(createdTime, 'dmy-time')) }}
      </MpText>
    </div>
    <MpIcon
      v-if="false"
      icon="close"
      class="notification-item__close"
      @click="emit('delete', item)"
    />
  </div>
</template>

<style scoped lang="scss">
  .notification-item {
    display: flex;
    gap: var(--mp-space-40);
    padding: var(--mp-space-50);
    padding-bottom: 0;

    &:first-of-type {
      margin-top: 0;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    &:hover {
      background-color: var(--mp-color-neutral-light);
    }

    &__dot {
      --dot-color: transparent;

      margin-left: var(--mp-space-30);
      margin-top: var(--mp-space-30);
      width: 3px;
      height: 3px;
      z-index: 1;
      border: 5px solid var(--dot-color);
      border-radius: var(--mp-radius-full);

      &--active {
        --dot-color: var(--mp-color-primary-default);
        @include dark-mode {
          --dot-color: var(--mp-color-primary-light);
        }
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-30);
      flex-grow: 2;
      border-bottom: 1px solid var(--mp-color-neutral-light);
      padding-bottom: var(--mp-space-50);
    }

    &__close {
      cursor: pointer;
      margin-right: var(--mp-space-40);
      height: min-content;
    }
  }
</style>
